import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

export default function AudioToTextHero() {
  const { t } = useLocale('ai-transcription');

  return (
    <div className='relative isolate overflow-hidden bg-white dark:bg-slate-900 pt-16 sm:pt-24 lg:pt-32 pb-20'>
      {/* Background elements */}
      <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808033_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]'></div>
      <div
        className='absolute left-1/2 top-0 -z-10 -translate-x-1/2 transform-gpu blur-3xl'
        aria-hidden='true'
      >
        <div
          className='aspect-[1108/632] w-[69.25rem] bg-gradient-to-tr from-indigo-200 to-primary-100 dark:from-slate-800 dark:to-slate-700 opacity-40 dark:opacity-20'
          style={{
            clipPath:
              'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
          }}
        />
      </div>

      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-3xl text-center'>
          <div className='mb-8 flex justify-center'>
            <div className='relative inline-flex items-center space-x-2 rounded-full px-4 py-1.5 text-sm font-medium leading-6 text-gray-600 dark:text-slate-300 ring-1 ring-gray-900/10 dark:ring-white/10 hover:ring-gray-900/20 dark:hover:ring-white/20 transition-all bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm'>
              <span className='flex h-2 w-2 rounded-full bg-primary-500 animate-pulse'></span>
              <span>{t('Audio to Text Converter')}</span>
            </div>
          </div>
          <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-6xl mb-6'>
            {t('Transcribe Audio to Text')}{' '}
            <span className='text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-pink-600 drop-shadow-sm'>
              {t('Online')}
            </span>
          </h1>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300 text-balance mx-auto max-w-2xl'>
            {t(
              'Convert your audio files to text instantly with our AI-powered transcription service. High accuracy, speaker detection, and support for 40+ languages.',
            )}
          </p>
          <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              href='/audios'
              className='group relative flex w-full sm:w-auto items-center justify-center rounded-full bg-primary-600 px-8 py-4 text-base font-bold text-white shadow-lg hover:bg-primary-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300'
            >
              {t('Start Transcribing')}
              <svg
                className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth='2'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                />
              </svg>
            </Link>
            <Link
              href='#how-it-works'
              className='flex w-full sm:w-auto items-center justify-center rounded-full px-8 py-4 text-base font-bold text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1'
            >
              {t('How it works')}
            </Link>
          </div>

          <div className='mt-8 flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-gray-500 dark:text-slate-400'>
            <div className='flex items-center'>
              <svg
                className='mr-2 h-5 w-5 text-green-500'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
              {t('No credit card required')}
            </div>
            <div className='flex items-center'>
              <svg
                className='mr-2 h-5 w-5 text-green-500'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
              {t('Free transcription minutes')}
            </div>
            <div className='flex items-center'>
              <svg
                className='mr-2 h-5 w-5 text-green-500'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
              {t('Secure & private')}
            </div>
          </div>
        </div>

        {/* Floating Screenshot */}
        <div className='mt-16 sm:mt-24 lg:mt-20 flex justify-center'>
          <div className='relative rounded-3xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 dark:bg-white/5 dark:ring-white/10 backdrop-blur-xl'>
            <div className='rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/10 dark:bg-slate-900 overflow-hidden'>
              <Image
                src='/images/transcription2.png'
                alt={t('Audio to Text Converter')}
                width={1000}
                height={600}
                className='w-full object-cover rounded-2xl'
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
